import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { 
    CloseCircleFilled, 
    CheckCircleFilled,
    PaperClipOutlined, 
    ProjectOutlined
} from '@ant-design/icons';
import { getTaskDescriptionMarkdown, getTaskDisplayId, isTaskOnDevBacklog } from '@hooks/projects/projectHelpers';
import { BacklogTaskBanner } from '@/components/sprint/BacklogTaskBanner';
import PhaseDescriptionAttachments from '@/components/module/projects/milestone_tab/PhaseDescriptionAttachments';
import Markdown from '@/components/ui/Markdown';
import {
    BoardGroupedSubmissionCard,
    BoardGroupedSubmissionSid,
    BoardGroupedSubmissionHeading,
} from '@/pages/inside/tenant/project/projectTabStyles';

const headerBtnReset = {
    display: 'block',
    width: '100%',
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
    color: 'inherit',
};

/**
 * One PMO kanban card: submission id + title, nested task rows (preview + TID).
 * Header click is `onOpenSubmission` (submit flow vs read-only detail — caller decides). Task rows call `onTaskClick`.
 */
export default function PMOSubmissionGroupCard({
    sidLabel,
    submissionTitle,
    sections,
    taskEntries = [],
    /** Submitted-to-client column: accessibility copy for view-only detail open. */
    viewSubmittedHeader = false,
    onOpenSubmission,
    onTaskClick,
    isQAPassed,
}) {
    const isGenericSubmission = useMemo(() => {
        if (!submissionTitle) return true;
        const lower = submissionTitle.toLowerCase().trim();
        return (
            lower === 'submission' ||
            lower.startsWith('submission ') ||
            /^submission\s+sub-\d+/i.test(lower) ||
            /^submission\s+sid-\d+/i.test(lower)
        );
    }, [submissionTitle]);

    const headerInner = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '2px 0 6px 0' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
                <BoardGroupedSubmissionSid as="span" style={{ marginBottom: isGenericSubmission ? 0 : 2, fontSize: '11px', color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.45))' }}>
                    {sidLabel}
                </BoardGroupedSubmissionSid>
                {!isGenericSubmission && (
                    <BoardGroupedSubmissionHeading as="span" style={{ fontSize: '14px', fontWeight: 650, color: 'var(--ant-color-text, rgba(0,0,0,0.85))' }}>
                        {submissionTitle}
                    </BoardGroupedSubmissionHeading>
                )}
            </div>
            {isQAPassed === true && (
                <div style={{ flexShrink: 0, marginLeft: 8 }}>
                    <Tooltip title="QA Passed" placement="topRight">
                        <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16, verticalAlign: 'middle' }} />
                    </Tooltip>
                </div>
            )}
            {isQAPassed === false && (
                <div style={{ flexShrink: 0, marginLeft: 8 }}>
                    <Tooltip title="Not QA Passed" placement="topRight">
                        <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16, verticalAlign: 'middle' }} />
                    </Tooltip>
                </div>
            )}
        </div>
    );

    const headerAria = viewSubmittedHeader
        ? `${sidLabel}${isGenericSubmission ? '' : `, ${submissionTitle}`}. View submission notes and tasks.`
        : `${sidLabel}${isGenericSubmission ? '' : `, ${submissionTitle}`}. Open submission details.`;

    const sectionList = Array.isArray(sections) ? sections.filter(Boolean) : [];

    const taskRows = useMemo(
        () =>
            (taskEntries ?? []).map((entry) => {
                const t = entry?.task;
                const idLabel =
                    t?.taskNumber || t?.task_number || t?.number || getTaskDisplayId(t);
                const desc = getTaskDescriptionMarkdown(t) || '';
                return {
                    key: entry?.key ?? `${idLabel}-${Math.random()}`,
                    entry,
                    idLabel,
                    desc,
                    attachmentItems: Array.isArray(entry?.attachmentItems) ? entry.attachmentItems : [],
                    isBacklog: isTaskOnDevBacklog(t),
                };
            }),
        [taskEntries],
    );

    return (
        <BoardGroupedSubmissionCard 
            className="pmo-submission-group-card" 
            style={{ 
                padding: '12px 14px', 
                border: '1px solid var(--ant-color-border-secondary, #e5e7eb)',
                boxShadow: '0 2px 5px rgba(0,0,0,0.02)',
                background: '#ffffff'
            }}
        >
            <button
                type="button"
                aria-label={headerAria}
                onClick={() => onOpenSubmission?.()}
                style={headerBtnReset}
            >
                {headerInner}
            </button>

            {sectionList.length > 0 ? (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {sectionList.map((sec) => {
                        const label = sec?.label ?? 'Submission';
                        const previewMarkdown =
                            typeof sec?.previewMarkdown === 'string' ? sec.previewMarkdown.trim() : '';
                        const items = Array.isArray(sec?.attachmentItems) ? sec.attachmentItems : [];
                        
                        // Decide accent line color based on submission type
                        let accentColor = '#d1d5db';
                        if (sec?.key === 'qa') accentColor = '#8b5cf6'; // Violet for QA
                        else if (sec?.key === 'developer') accentColor = '#3b82f6'; // Blue for Dev
                        else if (sec?.key === 'pmo') accentColor = '#0d9488'; // Teal for PMO

                        return (
                            <div
                                key={sec?.key ?? label}
                                style={{
                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                    borderLeft: `3px solid ${accentColor}`,
                                    borderRadius: '6px',
                                    padding: '8px 10px',
                                    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.015))',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div
                                            style={{
                                                fontSize: '10px',
                                                fontWeight: 750,
                                                letterSpacing: '0.04em',
                                                textTransform: 'uppercase',
                                                color: accentColor,
                                                marginBottom: 4,
                                                fontFamily: 'var(--font-family-geist)',
                                            }}
                                        >
                                            {label}
                                        </div>
                                        {previewMarkdown ? (
                                            <Markdown
                                                source={previewMarkdown}
                                                variant="compact"
                                                lineClamp={2}
                                                linkPrimary
                                                richInlineAnchors
                                                attachmentItems={items}
                                            />
                                        ) : (
                                            <div style={{ fontSize: 12, color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.45))', fontStyle: 'italic' }}>No notes provided</div>
                                        )}
                                    </div>
                                    {items.length > 0 ? (
                                        <div style={{ flexShrink: 0, marginTop: 2 }}>
                                            <PhaseDescriptionAttachments
                                                items={items}
                                                style={{ marginTop: 0 }}
                                                hideFileNames
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : null}

            {/* Structured Divider for Associated Tasks */}
            <div style={{ display: 'flex', alignItems: 'center', margin: '12px 0 8px 0', gap: 8 }}>
                <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.45))', letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <ProjectOutlined style={{ fontSize: '11px' }} />
                    Tasks ({taskRows.length})
                </span>
                <div style={{ flex: 1, height: '1px', background: 'var(--ant-color-border-secondary, #f0f0f0)' }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {taskRows.map((row) => {
                    return (
                        <button
                            key={row.key}
                            type="button"
                            data-pmo-task-row="true"
                            onClick={() => onTaskClick?.(row.entry)}
                            style={{
                                width: '100%',
                                minWidth: 0,
                                margin: 0,
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                background: '#fafafa',
                                textAlign: 'left',
                                cursor: 'pointer',
                                font: 'inherit',
                                color: 'inherit',
                                display: 'block',
                                transition: 'border-color 0.2s, background-color 0.2s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--ant-color-primary, #7c3aed)';
                                e.currentTarget.style.backgroundColor = '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--ant-color-border-secondary, #f0f0f0)';
                                e.currentTarget.style.backgroundColor = '#fafafa';
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                        <span style={{ 
                                            fontSize: '10px', 
                                            fontWeight: 700, 
                                            color: 'var(--ant-color-primary, #7c3aed)',
                                            background: 'rgba(124, 58, 237, 0.06)',
                                            padding: '1px 5px',
                                            borderRadius: '4px',
                                            border: '1px solid rgba(124, 58, 237, 0.15)',
                                            fontFamily: 'var(--font-family-geist)',
                                        }}>
                                            {row.idLabel}
                                        </span>
                                    </div>
                                    {row.isBacklog ? (
                                        <div style={{ marginBottom: 6 }}>
                                            <BacklogTaskBanner />
                                        </div>
                                    ) : null}
                                    {row.desc ? (
                                        <Markdown
                                            source={row.desc}
                                            variant="compact"
                                            lineClamp={2}
                                            linkPrimary
                                            richInlineAnchors
                                            attachmentItems={row.attachmentItems}
                                        />
                                    ) : (
                                        <div style={{ fontSize: 12, color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.45))', fontStyle: 'italic' }}>No description</div>
                                    )}
                                </div>
                                {row.attachmentItems.length > 0 ? (
                                    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.02)', padding: '2px 6px', borderRadius: '4px', border: '1px solid rgba(0,0,0,0.04)', marginTop: 2 }} onClick={(e) => e.stopPropagation()}>
                                        <PaperClipOutlined style={{ fontSize: '11px', color: 'rgba(0,0,0,0.45)' }} />
                                        <span style={{ fontSize: '10px', fontWeight: 600, color: 'rgba(0,0,0,0.55)', fontFamily: 'var(--font-family-geist)' }}>
                                            {row.attachmentItems.length}
                                        </span>
                                    </div>
                                ) : null}
                            </div>
                        </button>
                    );
                })}
            </div>
        </BoardGroupedSubmissionCard>
    );
}
